import React, { useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { cssX, SelectComponent } from "@datassential/platform-ui-lib";
import cn from "classnames";
import { components } from "@api/api";
import { useRestaurantMenu } from "./RestaurantMenuContext";
import RestaurantsMenuPopup from "@components/RestaurantsMenu/RestaurantsMenuPopup/RestaurantsMenuPopup";
import RestaurantsMenuManage from "@components/RestaurantsMenu/RestaurantsMenuManage/RestaurantsMenuManage";
import ConfirmationPopup from "@components/ConfirmationPopup/ConfirmationPopup";
import { useSearch } from "@core/customHooks";
import { addNotification } from "@actions/notifications.actions";
import { currentCountrySelector } from "@selectors/countries.selector";
import './RestaurantsMenu.styles.scss';

type CustomRestaurantListDetailsViewModel = components['schemas']['MenuTrends.Application.Contracts.Models.Restaurants.CustomRestaurantList.CustomRestaurantListDetailsViewModel'];

const RestaurantsMenu = () => {
    const [isRestaurantsMenuShown, setIsRestaurantsMenuShown] = useState(false);
    const [isRestaurantsMenuPopupShown, setIsRestaurantsMenuPopupShown] = useState(false);
    const [isManagePopupShown, setIsManagePopupShown] = useState(false);
    const [isViewPopupShown, setIsViewPopupShown] = useState(false);
    const [restaurantListId, setRestaurantListId] = useState<number | null>(null);
    const [isRemoveConfirmationShown, setIsRemoveConfirmationShown] = useState(false);
    const [isAdding, setIsAdding] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);

    const dispatch = useDispatch();
    const currentCountry = useSelector(currentCountrySelector);

    const {
        restaurantLists,
        customRestaurantLists,
        activeRestaurantListId,
        isLoadingRestaurantLists,
        isLoadingCustomRestaurantLists,
        setActiveRestaurantList,
        createCustomRestaurantList,
        updateCustomRestaurantList,
        deleteCustomRestaurantList,
    } = useRestaurantMenu();

    const [searchQuery, setSearchQuery, filteredSearchData] = useSearch(customRestaurantLists);

    const selectedRestaurant = useMemo(
        () => [
            ...restaurantLists,
            ...customRestaurantLists,
        ].find(r => r.id === activeRestaurantListId),
        [
            restaurantLists,
            customRestaurantLists,
            activeRestaurantListId,
        ]
    );

    const showNotification = useCallback((message: string, type: 'success' | 'error' = 'success') => {
        dispatch(addNotification({
            text: message,
            duration: 5000,
            [type]: true
        }));
    }, [dispatch]);

    const handleSelectRestaurant = useCallback((id: number) => {
        setActiveRestaurantList(id);
        setIsRestaurantsMenuShown(false);
    }, [setActiveRestaurantList]);

    const handleShowPopup = useCallback(() => {
        setIsRestaurantsMenuShown(false);
        setIsRestaurantsMenuPopupShown(true);
    }, []);

    const handleShowManagePopup = useCallback((id: number) => {
        setRestaurantListId(id);
        setIsRestaurantsMenuPopupShown(false);
        setIsManagePopupShown(true);
    }, []);

    const handleShowViewPopup = useCallback((id: number) => {
        setRestaurantListId(id);
        setIsRestaurantsMenuPopupShown(false);
        setIsViewPopupShown(true);
    }, []);

    const handleCloseManagePopup = useCallback((backward?: boolean) => {
        setRestaurantListId(null);
        setIsManagePopupShown(false);

        if (backward) {
            setIsRestaurantsMenuPopupShown(true);
        }
    }, []);

    const handleCloseViewPopup = useCallback((backward?: boolean) => {
        setRestaurantListId(null);
        setIsViewPopupShown(false);

        if (backward) {
            setIsRestaurantsMenuPopupShown(true);
        }
    }, []);

    const handleUpdateCustomList = useCallback(async (data: CustomRestaurantListDetailsViewModel, isEdit: boolean) => {
        if (isEdit) {
            setIsUpdating(true);
            try {
                await updateCustomRestaurantList(data);
                setIsUpdating(false);
                showNotification('Saved custom restaurant list');
                handleCloseManagePopup(true);
            } catch {
                setIsUpdating(false);
                showNotification('Save failed', 'error');
            }
        } else {
            setIsAdding(true);
            try {
                await createCustomRestaurantList(data);
                setIsAdding(false);
                showNotification('Created custom restaurant list');
                handleCloseManagePopup(true);
            } catch {
                setIsAdding(false);
                showNotification('Creating a new list failed', 'error');
            }
        }
    }, [updateCustomRestaurantList, createCustomRestaurantList, showNotification, handleCloseManagePopup]);

    const handleRemove = useCallback((id: number) => () => {
        setIsRemoveConfirmationShown(true);
        setRestaurantListId(id);
    }, []);

    const handleRemoveItemConfirm = useCallback(async () => {
        if (!restaurantListId) return;

        try {
            await deleteCustomRestaurantList(currentCountry, restaurantListId);
            setRestaurantListId(null);
            setIsRemoveConfirmationShown(false);
        } catch (err) {
            console.log(err);
        }
    }, [deleteCustomRestaurantList, currentCountry, restaurantListId]);

    return (
        <div className="RestaurantsMenu">
            {!isLoadingRestaurantLists && (
                <SelectComponent
                    thumbnailText={selectedRestaurant?.name?.toUpperCase()}
                    isOpened={isRestaurantsMenuShown}
                    setIsOpened={setIsRestaurantsMenuShown}
                    dropdownProps={{
                        isInPortal: true,
                        isOpened: isRestaurantsMenuShown,
                        place: "bottom-start",
                        columns: [{
                            id: 'id',
                            title: '',
                            items: [],
                            content: <>
                                {restaurantLists.map(i => (
                                    <div
                                        key={i.id}
                                        className={cn(cssX("Dropdown__row"))}
                                    >
                                        <div
                                            className={cn(
                                                cssX('Dropdown__item'),
                                                cssX('SelectComponent__item'),
                                                activeRestaurantListId === i.id && 'active-accessible',
                                            )}
                                            onClick={() => handleSelectRestaurant(i.id!)}
                                        >
                                            {i.name}
                                        </div>
                                    </div>
                                ))}

                                <div className={cn(cssX("Dropdown__row"))}>
                                    <div
                                        className={cn(
                                            cssX('Dropdown__item'),
                                            cssX('SelectComponent__item'),
                                        )}
                                        onClick={handleShowPopup}
                                    >
                                        Custom restaurant lists...
                                    </div>
                                </div>
                            </>,
                        }],
                    }}
                />
            )}

            <RestaurantsMenuPopup
                searchQuery={searchQuery}
                setSearchQuery={setSearchQuery}
                data={filteredSearchData}
                shown={isRestaurantsMenuPopupShown}
                handleItemClick={setActiveRestaurantList}
                closeModal={() => {
                    setIsRestaurantsMenuPopupShown(false);
                }}
                handleShowManagePopup={handleShowManagePopup}
                handleShowViewPopup={handleShowViewPopup}
                isLoadingCustomRestaurantLists={isLoadingCustomRestaurantLists}
                handleRemoveItem={handleRemove}
            />

            <RestaurantsMenuManage
                shown={isManagePopupShown}
                closeModal={handleCloseManagePopup}
                restaurantListId={restaurantListId}
                currentCountry={currentCountry}
                showNotification={showNotification}
                handleUpdateCustomList={handleUpdateCustomList}
                isUpdating={isUpdating || isAdding}
            />

            <RestaurantsMenuManage
                shown={isViewPopupShown}
                closeModal={handleCloseViewPopup}
                restaurantListId={restaurantListId}
                currentCountry={currentCountry}
                showNotification={showNotification}
                handleUpdateCustomList={() => null}
                isUpdating={false}
                isViewMode
            />

            <ConfirmationPopup
                shown={isRemoveConfirmationShown}
                closeModal={() => setIsRemoveConfirmationShown(false)}
                title="Are you sure you want to delete this restaurant list?"
                text={null}
                onConfirm={handleRemoveItemConfirm}
                submitButtonText="Delete"
            />
        </div>
    );
};

export default RestaurantsMenu;